import api from './api'

export const COMMERCIAL_ROLES = [
  'main',
  'drink',
  'side',
  'dessert',
  'accessory',
  'warranty',
  'service',
  'substitute',
] as const

export const RELATION_TYPES = [
  'compatible',
  'complement',
  'bundle',
  'upsell',
  'cross_sell',
  'substitute',
] as const

export type CommercialRole = (typeof COMMERCIAL_ROLES)[number]
export type CatalogRelationType = (typeof RELATION_TYPES)[number]

export interface CatalogIntelligenceCategory {
  id: string
  name: string
  slug?: string | null
  description?: string | null
  parentId?: string | null
  sortOrder?: number
  isActive?: boolean
  productCount?: number
  commercialRole?: CommercialRole | string | null
  children?: CatalogIntelligenceCategory[]
}

export interface CatalogIntelligenceProduct {
  id: string
  sku: string
  slug?: string | null
  name: string
  priceCents?: number
  currency?: string
  stock?: number
  commercialRole?: CommercialRole | string | null
  categoryId?: string | null
  category?: {
    id: string
    name: string
    slug?: string | null
    commercialRole?: CommercialRole | string | null
  } | null
}

export interface CatalogRelationEndpoint {
  id: string
  name: string
  sku?: string | null
  slug?: string | null
}

export interface ProductCatalogRelation {
  id: string
  sourceProductId: string
  targetProductId: string
  relationType: CatalogRelationType | string
  priority: number
  vertical?: string | null
  isActive: boolean
  metadata?: Record<string, unknown> | null
  sourceProduct?: CatalogRelationEndpoint | null
  targetProduct?: CatalogRelationEndpoint | null
}

export interface CategoryCatalogRelation {
  id: string
  sourceCategoryId: string
  targetCategoryId: string
  relationType: CatalogRelationType | string
  priority: number
  vertical?: string | null
  isActive: boolean
  metadata?: Record<string, unknown> | null
  sourceCategory?: CatalogRelationEndpoint | null
  targetCategory?: CatalogRelationEndpoint | null
}

export type ProductRelationInput = {
  sourceProductId: string
  targetProductId: string
  relationType: CatalogRelationType
  priority: number
  vertical?: string | null
  isActive: boolean
  metadata?: Record<string, unknown> | null
}

export type CategoryRelationInput = {
  sourceCategoryId: string
  targetCategoryId: string
  relationType: CatalogRelationType
  priority: number
  vertical?: string | null
  isActive: boolean
  metadata?: Record<string, unknown> | null
}

export interface CatalogCoverageReport {
  totalProducts: number
  totalCategories: number
  totalRelations: number
  productRoleCoveragePercent: number
  categoryRoleCoveragePercent: number
  productsWithoutRole: CatalogIntelligenceProduct[]
  categoriesWithoutRole: CatalogIntelligenceCategory[]
  relationsWithoutUsefulMetadata: Array<ProductCatalogRelation | CategoryCatalogRelation>
  bundlesWithoutPriority: Array<ProductCatalogRelation | CategoryCatalogRelation>
}

const COMMERCIAL_METADATA_KEYS = new Set([
  'role',
  'targetRole',
  'sourceRole',
  'bundleRole',
  'commercialRole',
  'categoryRole',
  'reason',
  'compatibility',
  'compatibleWith',
  'attachTo',
  'slot',
  'bundleSlot',
  'minQuantity',
  'maxQuantity',
  'menuSection',
])

export function flattenCategories(categories: CatalogIntelligenceCategory[]): CatalogIntelligenceCategory[] {
  const flat: CatalogIntelligenceCategory[] = []
  const visit = (category: CatalogIntelligenceCategory) => {
    flat.push(category)
    for (const child of category.children ?? []) {
      visit(child)
    }
  }
  for (const category of categories) visit(category)
  return flat
}

export function getEffectiveProductRole(product: CatalogIntelligenceProduct): string | null {
  return product.commercialRole || product.category?.commercialRole || null
}

export function hasUsefulRelationMetadata(metadata: Record<string, unknown> | null | undefined): boolean {
  if (!metadata || typeof metadata !== 'object') return false
  const entries = Object.entries(metadata)
  if (entries.length === 0) return false

  return entries.some(([key, value]) => {
    if (value == null || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    if (key === 'migratedFrom' || key === 'sourceFields' || key === 'sourceProductId') return false
    return COMMERCIAL_METADATA_KEYS.has(key) || typeof value === 'boolean' || typeof value === 'number'
  })
}

export function buildCatalogCoverageReport(params: {
  products: CatalogIntelligenceProduct[]
  categories: CatalogIntelligenceCategory[]
  productRelations: ProductCatalogRelation[]
  categoryRelations: CategoryCatalogRelation[]
}): CatalogCoverageReport {
  const flatCategories = flattenCategories(params.categories)
  const allRelations = [...params.productRelations, ...params.categoryRelations]
  const productsWithoutRole = params.products.filter((product) => !getEffectiveProductRole(product))
  const categoriesWithoutRole = flatCategories.filter((category) => !category.commercialRole)
  const relationsWithoutUsefulMetadata = allRelations.filter((relation) => !hasUsefulRelationMetadata(relation.metadata))
  const bundlesWithoutPriority = allRelations.filter(
    (relation) => relation.relationType === 'bundle' && Number(relation.priority ?? 0) <= 0
  )

  return {
    totalProducts: params.products.length,
    totalCategories: flatCategories.length,
    totalRelations: allRelations.length,
    productRoleCoveragePercent: percent(params.products.length - productsWithoutRole.length, params.products.length),
    categoryRoleCoveragePercent: percent(flatCategories.length - categoriesWithoutRole.length, flatCategories.length),
    productsWithoutRole,
    categoriesWithoutRole,
    relationsWithoutUsefulMetadata,
    bundlesWithoutPriority,
  }
}

function percent(value: number, total: number): number {
  if (total <= 0) return 100
  return Math.round((value / total) * 100)
}

export async function listAllCatalogProducts(): Promise<CatalogIntelligenceProduct[]> {
  const firstPage = await api.get<{ items: CatalogIntelligenceProduct[]; total: number; pageSize: number }>('/catalog/items', {
    params: { page: 1, pageSize: 100, orderBy: 'name', order: 'asc' },
  })
  const items = [...firstPage.data.items]
  const total = Number(firstPage.data.total ?? items.length)
  const pageSize = Number(firstPage.data.pageSize ?? 100)
  const totalPages = Math.ceil(total / pageSize)

  for (let page = 2; page <= totalPages; page += 1) {
    const { data } = await api.get<{ items: CatalogIntelligenceProduct[] }>('/catalog/items', {
      params: { page, pageSize, orderBy: 'name', order: 'asc' },
    })
    items.push(...data.items)
  }

  return items
}

export async function listCatalogCategories(): Promise<CatalogIntelligenceCategory[]> {
  const { data } = await api.get<CatalogIntelligenceCategory[]>('/catalog/categories')
  return data
}

export async function updateProductCommercialRole(id: string, commercialRole: string | null): Promise<CatalogIntelligenceProduct> {
  const { data } = await api.put<CatalogIntelligenceProduct>(`/catalog/items/${id}`, { commercialRole })
  return data
}

export async function updateCategoryCommercialRole(id: string, commercialRole: string | null): Promise<CatalogIntelligenceCategory> {
  const { data } = await api.put<CatalogIntelligenceCategory>(`/catalog/categories/${id}`, { commercialRole })
  return data
}

export async function listProductRelations(sourceProductId?: string): Promise<ProductCatalogRelation[]> {
  const { data } = await api.get<{ items: ProductCatalogRelation[] }>(
    '/catalog/relations/products',
    sourceProductId ? { params: { sourceProductId } } : undefined
  )
  return data.items
}

export async function upsertProductRelation(input: ProductRelationInput): Promise<ProductCatalogRelation> {
  const { data } = await api.post<ProductCatalogRelation>('/catalog/relations/products', input)
  return data
}

export async function deleteProductRelation(id: string): Promise<void> {
  await api.delete(`/catalog/relations/products/${id}`)
}

export async function listCategoryRelations(): Promise<CategoryCatalogRelation[]> {
  const { data } = await api.get<{ items: CategoryCatalogRelation[] }>('/catalog/relations/categories')
  return data.items
}

export async function upsertCategoryRelation(input: CategoryRelationInput): Promise<CategoryCatalogRelation> {
  const { data } = await api.post<CategoryCatalogRelation>('/catalog/relations/categories', input)
  return data
}

export async function deleteCategoryRelation(id: string): Promise<void> {
  await api.delete(`/catalog/relations/categories/${id}`)
}

export async function syncRelationsFromDirectives(): Promise<{
  scannedProducts: number
  productRelationsUpserted: number
  categoryRelationsUpserted: number
}> {
  const { data } = await api.post('/catalog/relations/sync-directives')
  return data
}
